/**
 * 源石币配置管理 — GET/POST/PATCH /api/admin/uoc/configs + GET /supply + POST /recharge
 *
 * Admin CRUD for UOC conversion configs (one row per target token: rp/tp/ep).
 * Each config controls pool_balance, rate, and is_active for that burn path.
 * Also shows global UOC supply and provides a recharge action.
 */
import { useState, useEffect } from 'react';
import {
  Button,
  Card,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Statistic,
  Switch,
  Tag,
  message,
} from 'antd';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface ConfigRow {
  id: number;
  target_token: string;
  pool_balance: string;
  rate: string;
  is_active: boolean;
  note: string | null;
  create_time: string;
  update_time: string;
}

interface SupplyData {
  total_supply: string;
  current_supply: string;
  update_time: string;
}

interface ConfigForm {
  target_token: string;
  pool_balance: string;
  rate: string;
  is_active: boolean;
  note?: string;
}

interface RechargeForm {
  amount: string;
  note?: string;
}

const TOKEN_OPTIONS = [
  { label: 'RP (推荐积分)', value: 'rp' },
  { label: 'TP (团队积分)', value: 'tp' },
  { label: 'EP (算力包)', value: 'ep' },
];

const TOKEN_LABEL: Record<string, string> = {
  rp: 'RP',
  tp: 'TP',
  ep: 'EP',
};

export function UocConfigPage() {
  const qc = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<ConfigRow | null>(null);
  const [rechargeOpen, setRechargeOpen] = useState(false);
  const [createForm] = Form.useForm<ConfigForm>();
  const [editForm] = Form.useForm<ConfigForm>();
  const [rechargeForm] = Form.useForm<RechargeForm>();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canCreate = hasNode('uoc.config.create');
  const canEdit = hasNode('uoc.config.update');
  const canRecharge = hasNode('uoc.recharge');

  const { data: supply, refetch: refetchSupply } = useQuery<SupplyData>({
    queryKey: ['admin', 'uoc-supply'],
    queryFn: async () => {
      const res = await api.get<SupplyData>('/api/admin/uoc/supply');
      return res;
    },
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ['admin', 'uoc-configs'] });
    refetchSupply();
  }

  async function onCreate(values: ConfigForm) {
    try {
      await api.post('/api/admin/uoc/configs', values);
      message.success('配置已创建');
      setCreateOpen(false);
      createForm.resetFields();
      invalidate();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '创建失败');
    }
  }

  async function onEdit(values: ConfigForm) {
    if (!editRow) return;
    try {
      const patch: Record<string, unknown> = {};
      if (values.pool_balance !== editRow.pool_balance) patch.pool_balance = values.pool_balance;
      if (values.rate !== editRow.rate) patch.rate = values.rate;
      if (values.is_active !== editRow.is_active) patch.is_active = values.is_active;
      if ((values.note ?? '') !== (editRow.note ?? '')) patch.note = values.note;
      await api.patch(`/api/admin/uoc/configs/${editRow.id}`, patch);
      message.success('配置已更新');
      setEditRow(null);
      invalidate();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '更新失败');
    }
  }

  async function onRecharge(values: RechargeForm) {
    try {
      await api.post('/api/admin/uoc/recharge', values);
      message.success('充值成功');
      setRechargeOpen(false);
      rechargeForm.resetFields();
      invalidate();
    } catch (err) {
      message.error(err instanceof ApiError ? err.message : '充值失败');
    }
  }

  useEffect(() => {
    if (editRow) {
      editForm.setFieldsValue({
        target_token: editRow.target_token,
        pool_balance: editRow.pool_balance,
        rate: editRow.rate,
        is_active: editRow.is_active,
        note: editRow.note ?? '',
      });
    }
  }, [editRow, editForm]);

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    {
      title: '目标代币',
      dataIndex: 'target_token',
      width: 120,
      render: (v: string) => (
        <Tag color="blue">{TOKEN_LABEL[v] ?? v.toUpperCase()}</Tag>
      ),
    },
    {
      title: '兑换池余额',
      dataIndex: 'pool_balance',
      width: 160,
      render: (v: string) => Number(v).toLocaleString(),
    },
    {
      title: '兑换率 (UOC→目标)',
      dataIndex: 'rate',
      width: 160,
      render: (v: string) => v,
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      width: 80,
      render: (v: boolean) => (
        <Tag color={v ? 'green' : 'default'}>{v ? '启用' : '停用'}</Tag>
      ),
    },
    { title: '备注', dataIndex: 'note', ellipsis: true, width: 200 },
    {
      title: '更新时间',
      dataIndex: 'update_time',
      width: 180,
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: '操作',
      key: 'actions',
      width: 100,
      render: (_: unknown, row: ConfigRow) =>
        canEdit ? (
          <Button type="link" size="small" onClick={() => setEditRow(row)}>
            编辑
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Supply overview card */}
      <Card title="源石币 (UOC) 全局概览" size="small">
        <Space size="large">
          <Statistic
            title="总供应量"
            value={supply ? Number(supply.total_supply).toLocaleString() : '—'}
          />
          <Statistic
            title="当前流通量"
            value={supply ? Number(supply.current_supply).toLocaleString() : '—'}
          />
          <Statistic
            title="最后更新"
            value={supply ? new Date(supply.update_time).toLocaleString() : '—'}
          />
          {canRecharge && (
            <Button type="primary" onClick={() => setRechargeOpen(true)}>
              充值 UOC 供应
            </Button>
          )}
        </Space>
      </Card>

      {/* Config table */}
      {canCreate && (
        <Space>
          <Button type="primary" onClick={() => setCreateOpen(true)}>
            新建配置
          </Button>
        </Space>
      )}

      <DataTable<ConfigRow>
        queryKey={['admin', 'uoc-configs']}
        endpoint="/api/admin/uoc/configs"
        rowKey="id"
        columns={columns}
      />

      {/* Create modal */}
      <Modal
        title="新建 UOC 兑换配置"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        destroyOnClose
      >
        <Form form={createForm} onFinish={onCreate} layout="vertical">
          <Form.Item name="target_token" label="目标代币" rules={[{ required: true }]}>
            <Select options={TOKEN_OPTIONS} placeholder="选择目标代币" />
          </Form.Item>
          <Form.Item name="pool_balance" label="初始池余额" rules={[{ required: true }]}>
            <Input placeholder="例如：1000000" />
          </Form.Item>
          <Form.Item name="rate" label="兑换率 (1 UOC = ? 目标)" rules={[{ required: true }]}>
            <Input placeholder="例如：1.5" />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit modal */}
      <Modal
        title={`编辑 UOC→${TOKEN_LABEL[editRow?.target_token ?? ''] ?? ''} 配置`}
        open={!!editRow}
        onCancel={() => setEditRow(null)}
        onOk={() => editForm.submit()}
        destroyOnClose
      >
        <Form form={editForm} onFinish={onEdit} layout="vertical">
          <Form.Item name="target_token" label="目标代币">
            <Select options={TOKEN_OPTIONS} disabled />
          </Form.Item>
          <Form.Item name="pool_balance" label="池余额" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="rate" label="兑换率" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Recharge modal */}
      <Modal
        title="充值 UOC 供应"
        open={rechargeOpen}
        onCancel={() => setRechargeOpen(false)}
        onOk={() => rechargeForm.submit()}
        destroyOnClose
      >
        <Form form={rechargeForm} onFinish={onRecharge} layout="vertical">
          <Form.Item name="amount" label="充值金额" rules={[{ required: true }]}>
            <Input placeholder="例如：1000000" />
          </Form.Item>
          <Form.Item name="note" label="备注">
            <Input.TextArea rows={2} maxLength={500} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
